import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import {
  ChevronRight,
  FileText,
  Paperclip,
  RefreshCw,
} from "lucide-react";
import api from "../../config/configApi";

type ClassificacaoAnexo = {
  idClassificacaoAnexo: number;
  descricao: string;
  ativo: boolean;
};

type AnexoProntuario = {
  idAnexo: number;
  idEvolucao: number;
  nuCpf: string;
  classificacao: ClassificacaoAnexo;
  nomeArquivoOriginal: string;
  nomeArquivoGerado: string;
  caminhoRelativo: string;
  contentType: string;
  tamanhoBytes: number;
  dtHraUpload: string;
  dtHraEvolucao: string;
};

type ProntuarioAnexosProps = {
  prontuarioId: number | null;
  variant?: "button" | "card";
  listMode?: "event" | "cpf";
  refreshKey?: number;
  onUploadSuccess?: () => void;
};

const formatarTamanho = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatarDataHora = (valor?: string) => {
  if (!valor) return "--";
  return new Date(valor).toLocaleString("pt-BR");
};

const ANEXOS_POR_PAGINA = 4;

const normalizarCpf = (cpf?: string | null) => (cpf || "").replace(/\D/g, "");
const extrairListaAnexos = (payload: any): AnexoProntuario[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.resultado)) return payload.resultado;
  return [];
};

const obterToken = () => {
  // O projeto usa localStorage("Frontend") via interceptor, mas algumas telas usam sessionStorage("token").
  return (
    sessionStorage.getItem("token") ||
    localStorage.getItem("Frontend") ||
    sessionStorage.getItem("Frontend") ||
    null
  );
};

const ProntuarioAnexos: React.FC<ProntuarioAnexosProps> = ({
  prontuarioId,
  variant = "card",
  listMode = "event",
  refreshKey = 0,
  onUploadSuccess,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [classificacaoSelecionada, setClassificacaoSelecionada] = useState<
    number | null
  >(null);
  const [classificacoes, setClassificacoes] = useState<ClassificacaoAnexo[]>([]);
  const [anexos, setAnexos] = useState<AnexoProntuario[]>([]);
  const [carregandoAnexos, setCarregandoAnexos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [expandido, setExpandido] = useState(false);

  const token = obterToken();

  const carregarClassificacoes = useCallback(async () => {
    if (classificacoes.length > 0) {
      return classificacoes;
    }

    const { data } = await api.get<ClassificacaoAnexo[]>(
      "/sisclinic/prontuarios/anexos/classificacoes",
      {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );

    const classificacoesAtivas = (data || []).filter((item) => item.ativo);
    setClassificacoes(classificacoesAtivas);
    return classificacoesAtivas;
  }, [classificacoes, token]);

  const carregarAnexos = useCallback(async () => {
    if (listMode === "cpf") {
      const cpf = normalizarCpf(Cookies.get("NuCPF"));

      if (!cpf) {
        setAnexos([]);
        return;
      }

      try {
        setCarregandoAnexos(true);

        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        // Endpoint oficial: consultar anexos do beneficiário por CPF.
        try {
          const { data } = await api.get(
            `/sisclinic/prontuarios/cpf/${cpf}/anexos`,
            { headers }
          );
          const anexosCpf = extrairListaAnexos(data);
          if (anexosCpf.length > 0) {
            const anexosUnicosCpf = Array.from(
              new Map(
                anexosCpf.map((anexo) => [String(anexo.idAnexo), anexo])
              ).values()
            ).sort(
              (a, b) =>
                new Date(b.dtHraUpload || 0).getTime() -
                new Date(a.dtHraUpload || 0).getTime()
            );

            setAnexos(anexosUnicosCpf);
            setPaginaAtual(1);
            return;
          }
        } catch (error) {
          console.error(
            "Falha ao consultar anexos por CPF no endpoint principal:",
            error
          );
        }

        const [resProntuarios, resAnamnese] = await Promise.allSettled([
          api.get<{ idEvolucao: number }[]>(`/sisclinic/prontuarios/cpf/${cpf}`, {
            headers,
          }),
          api.get<{ idEvolucao: number }[]>(`/sisclinic/anamnese/cpf/${cpf}`, {
            headers,
          }),
        ]);

        const evolucoesProntuarios =
          resProntuarios.status === "fulfilled" &&
          Array.isArray(resProntuarios.value.data)
            ? resProntuarios.value.data
            : [];

        const evolucoesAnamnese =
          resAnamnese.status === "fulfilled" &&
          Array.isArray(resAnamnese.value.data)
            ? resAnamnese.value.data
            : [];

        const idsEvolucao = Array.from(
          new Set(
            [...evolucoesProntuarios, ...evolucoesAnamnese, ...(prontuarioId ? [{ idEvolucao: prontuarioId }] : [])]
              .map((item) => Number(item?.idEvolucao))
              .filter((id) => Number.isFinite(id) && id > 0)
          )
        );

        const anexosDiretosCpf: AnexoProntuario[] = [];
        const endpointsDiretosCpf = [
          `/sisclinic/prontuarios/anexos/cpf/${cpf}`,
          `/sisclinic/prontuarios/cpf/${cpf}/anexos`,
          `/sisclinic/anexos/cpf/${cpf}`,
        ];

        for (const endpoint of endpointsDiretosCpf) {
          try {
            const { data } = await api.get<AnexoProntuario[] | { content?: AnexoProntuario[] }>(
              endpoint,
              { headers }
            );
            anexosDiretosCpf.push(...extrairListaAnexos(data));
          } catch {
            // Endpoint pode nÃ£o existir neste backend, segue para o prÃ³ximo.
          }
        }

        if (idsEvolucao.length === 0) {
          setAnexos(
            Array.from(
              new Map(
                anexosDiretosCpf.map((anexo) => [String(anexo.idAnexo), anexo])
              ).values()
            ).sort(
              (a, b) =>
                new Date(b.dtHraUpload || 0).getTime() -
                new Date(a.dtHraUpload || 0).getTime()
            )
          );
          setPaginaAtual(1);
          return;
        }

        // Sequencial para evitar perda de resposta por limite de concorrência no backend.
        const anexosAgrupados: AnexoProntuario[] = [];
        for (const idEvolucao of idsEvolucao) {
          try {
            const { data } = await api.get<AnexoProntuario[]>(
              `/sisclinic/prontuarios/${idEvolucao}/anexos`,
              { headers }
            );
            anexosAgrupados.push(...extrairListaAnexos(data));
          } catch (error) {
            console.error(
              `Erro ao buscar anexos do prontuário ${idEvolucao}:`,
              error
            );
          }
        }

        const anexosUnicos = Array.from(
          new Map(
            [...anexosDiretosCpf, ...anexosAgrupados].map((anexo) => [
              String(anexo.idAnexo),
              anexo,
            ])
          ).values()
        )
          .sort(
            (a, b) =>
              new Date(b.dtHraUpload || 0).getTime() -
              new Date(a.dtHraUpload || 0).getTime()
          );

        setAnexos(anexosUnicos);
        setPaginaAtual(1);
      } catch (error) {
        console.error("Erro ao buscar anexos por CPF:", error);
        setAnexos([]);
      } finally {
        setCarregandoAnexos(false);
      }

      return;
    }

    if (!prontuarioId) {
      setAnexos([]);
      return;
    }

    try {
      setCarregandoAnexos(true);
      const { data } = await api.get<AnexoProntuario[]>(
        `/sisclinic/prontuarios/${prontuarioId}/anexos`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      setAnexos(Array.isArray(data) ? data : []);
      setPaginaAtual(1);
    } catch (error) {
      console.error("Erro ao buscar anexos do prontuário:", error);
      setAnexos([]);
    } finally {
      setCarregandoAnexos(false);
    }
  }, [listMode, prontuarioId, token]);

  useEffect(() => {
    if (variant === "card" && expandido) {
      carregarAnexos();
    }
  }, [carregarAnexos, expandido, refreshKey, variant]);

  const abrirSeletorArquivos = async () => {
    if (!prontuarioId) {
      await Swal.fire({
        title: "Prontuário não salvo",
        text: "Salve o prontuário para anexar arquivos.",
        icon: "info",
      });
      return;
    }

    try {
      const classificacoesDisponiveis = await carregarClassificacoes();

      if (classificacoesDisponiveis.length === 0) {
        await Swal.fire({
          title: "Classificações não encontradas",
          text: "Não foi possível carregar as classificações de anexo.",
          icon: "warning",
        });
        return;
      }

      const inputOptions = classificacoesDisponiveis.reduce<
        Record<string, string>
      >((acc, item) => {
        acc[String(item.idClassificacaoAnexo)] = item.descricao;
        return acc;
      }, {});

      const resultado = await Swal.fire({
        title: "Escolha a classificação",
        input: "select",
        inputOptions,
        inputPlaceholder: "Selecione a classificação",
        showCancelButton: true,
        confirmButtonText: "Selecionar arquivos",
        cancelButtonText: "Cancelar",
        inputValidator: (value) => {
          if (!value) {
            return "Selecione uma classificação.";
          }
          return null;
        },
      });

      if (!resultado.isConfirmed) {
        return;
      }

      setClassificacaoSelecionada(Number(resultado.value));
      inputRef.current?.click();
    } catch (error) {
      console.error("Erro ao preparar upload de anexos:", error);
      await Swal.fire({
        title: "Erro ao carregar classificações",
        text: "Não foi possível preparar o envio dos anexos.",
        icon: "error",
      });
    }
  };

  const enviarArquivos = async (files: FileList | null) => {
    if (!files || files.length === 0 || !prontuarioId || !classificacaoSelecionada) {
      return;
    }

    try {
      setEnviando(true);

      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("arquivo", file);

        await api.post(
          `/sisclinic/prontuarios/${prontuarioId}/anexos?idClassificacaoAnexo=${classificacaoSelecionada}`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
      }

      await Swal.fire({
        title: "Anexos enviados",
        text: "Os arquivos foram anexados com sucesso ao prontuário.",
        icon: "success",
      });

      await carregarAnexos();
      onUploadSuccess?.();
    } catch (error) {
      console.error("Erro ao enviar anexos:", error);
      await Swal.fire({
        title: "Erro ao enviar anexos",
        text: "Não foi possível anexar os arquivos ao prontuário.",
        icon: "error",
      });
    } finally {
      setEnviando(false);
      setClassificacaoSelecionada(null);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const abrirAnexo = async (anexo: AnexoProntuario) => {
    const prontuarioDownloadId = anexo.idEvolucao || prontuarioId;

    if (!prontuarioDownloadId) {
      return;
    }

    try {
      const { data } = await api.get(
        `/sisclinic/prontuarios/${prontuarioDownloadId}/anexos/${anexo.idAnexo}/download`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          responseType: "blob",
        }
      );

      const blob = new Blob([data], {
        type: anexo.contentType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("Erro ao baixar anexo:", error);
      await Swal.fire({
        title: "Erro ao baixar anexo",
        text: "Não foi possível baixar o arquivo selecionado.",
        icon: "error",
      });
    }
  };

  if (variant === "button") {
    return (
      <>
        <button
          type="button"
          onClick={abrirSeletorArquivos}
          disabled={enviando || !prontuarioId}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-semibold shadow transition ${
            enviando || !prontuarioId
              ? "bg-red-200 text-red-900 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }`}
        >
          <Paperclip className="w-4 h-4" />
          {!prontuarioId
            ? "Salve o prontuario para anexar"
            : enviando
              ? "Enviando anexos..."
              : "Adicionar anexos"}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => enviarArquivos(event.target.files)}
        />
      </>
    );
  }

  const totalPaginas = Math.max(
    1,
    Math.ceil(anexos.length / ANEXOS_POR_PAGINA)
  );
  const anexosPaginados = anexos.slice(
    (paginaAtual - 1) * ANEXOS_POR_PAGINA,
    paginaAtual * ANEXOS_POR_PAGINA
  );

  return (
    <div className="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg border border-gray-100">
      <button
        type="button"
        onClick={() => setExpandido((prev) => !prev)}
        className="w-full text-left text-sm sm:text-base font-bold border-b border-gray-200 pb-2 mb-2 flex items-center justify-between"
      >
        <span className="flex items-center gap-2 text-xs sm:text-sm font-bold text-violet-600">
          <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
          Anexos
        </span>
        <ChevronRight
          className={`h-4 w-4 text-violet-600 transition-transform duration-300 ${
            expandido ? "rotate-90" : "rotate-0"
          }`}
        />
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => enviarArquivos(event.target.files)}
      />

      {!expandido ? null : !prontuarioId ? (
        <p className="text-xs text-gray-500 italic">
          Salve o prontuário para visualizar e anexar arquivos.
        </p>
      ) : carregandoAnexos ? (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={carregarAnexos}
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3 h-3" />
              Atualizar
            </button>
          </div>
          <p className="text-xs text-gray-500 italic animate-pulse">
            Carregando anexos...
          </p>
        </div>
      ) : anexos.length === 0 ? (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={carregarAnexos}
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3 h-3" />
              Atualizar
            </button>
          </div>
          <p className="text-xs text-gray-500 italic">
            Nenhum anexo encontrado para este prontuário.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={carregarAnexos}
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="w-3 h-3" />
              Atualizar
            </button>
          </div>

          {anexosPaginados.map((anexo) => (
            <div
              key={anexo.idAnexo}
              onClick={() => abrirAnexo(anexo)}
              className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 cursor-pointer transition hover:bg-gray-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-semibold text-gray-800">
                    {anexo.nomeArquivoOriginal}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {anexo.classificacao?.descricao || "Sem classificação"}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {formatarTamanho(anexo.tamanhoBytes)} •{" "}
                    {formatarDataHora(anexo.dtHraUpload)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    abrirAnexo(anexo);
                  }}
                  title="Abrir anexo"
                  aria-label="Abrir anexo"
                  className="inline-flex items-center justify-center rounded-md bg-blue-100 p-1.5 text-blue-700 hover:bg-blue-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {anexos.length > ANEXOS_POR_PAGINA && (
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.max(1, prev - 1))}
                disabled={paginaAtual === 1}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-semibold transition ${
                  paginaAtual === 1
                    ? "cursor-not-allowed bg-gray-200 text-gray-400"
                    : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                }`}
              >
                Anterior
              </button>

              <span className="text-[11px] text-gray-500">
                Página {paginaAtual} de {totalPaginas}
              </span>

              <button
                type="button"
                onClick={() =>
                  setPaginaAtual((prev) => Math.min(totalPaginas, prev + 1))
                }
                disabled={paginaAtual === totalPaginas}
                className={`rounded-lg px-3 py-1.5 text-[11px] font-semibold transition ${
                  paginaAtual === totalPaginas
                    ? "cursor-not-allowed bg-gray-200 text-gray-400"
                    : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                }`}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProntuarioAnexos;
